/**
 * chatScript - the author's rule-based flirty dialogue engine (pure, no LLM).
 *
 * Four layers keep a user engaged for a long time without any model:
 *   1. intent detection - keyword/regex classification of the user's message
 *   2. response banks    - many varied replies per intent (+ anti-repeat)
 *   3. storyline (BEATS) - questions she proactively weaves in to drive an arc
 *   4. affection gating  - photos/calls are EARNED: low affection or too few
 *                          asks -> she teases/defers ("let me get ready"...).
 *
 * Off-script messages never break the conversation: small-talk + follow-up
 * questions keep the user typing, while the storyline advances opportunistically.
 *
 * Pure function of (chat, userMsg) -> next author action + updated state. The
 * caller (chat.js) persists step/flags/affection/photoRequests/photosSent/
 * lastLine. Timing (typing delay) lives in the hook/UI layer, not here.
 *
 * @typedef {Object} AuthorAction
 * @property {string}  text
 * @property {?Object} media
 * @property {boolean} offersCall
 * @property {Object}  flags
 * @property {number}  affection
 * @property {number}  photoRequests
 * @property {number}  photosSent
 * @property {number}  step
 * @property {string}  lastLine
 */
import { randomAuthorPhoto } from './authorMedia.js';

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const sample = (arr) => arr[Math.floor(Math.random() * arr.length)];
/** Random element, avoiding `avoid` when possible (anti-repetition). */
const pick = (arr, avoid) => {
  if (arr.length <= 1) return arr[0];
  let v = sample(arr);
  for (let i = 0; v === avoid && i < 4; i++) v = sample(arr);
  return v;
};

/* ------------------------------------------------------------------ *
 * Keyword reactions - short lead-ins keyed by detected intent.
 * They are PREPENDED to the beat line; the beat line itself is fixed.
 * ------------------------------------------------------------------ */
const KEYWORDS = [
  {
    test: /\b(hi|hey|hello|hallo|yo|sup|privet|привет)\b/i,
    flag: 'greeted',
    leads: ['Heyy 😊 ', 'Well hello there 😏 ', 'Hi you 💕 '],
  },
  {
    test: /\b(beautiful|gorgeous|sexy|hot|cute|pretty|stunning|wow|красив|сексуальн)\b/i,
    flag: 'complimented',
    leads: ['Awww you flatter me 🙈 ', 'Mmm I love hearing that 😘 ', 'You\u2019re making me blush 💗 '],
  },
  {
    test: /\b(work|working|study|gym|home|chilling|relax|gaming|tired|bored|eat|sleep)\b/i,
    flag: 'today',
    leads: ['Oooh sounds nice 😌 ', 'Mmm interesting 😏 ', 'I love that 💫 '],
  },
  {
    test: /\b(photo|pic|picture|selfie|see you|show me|nudes?|фото|фотк)\b/i,
    flag: 'photo',
    leads: ['Impatient, are we? 😏 ', 'Hehe someone wants more 🙈 ', 'You read my mind 💋 '],
  },
  {
    test: /\b(call|voice|video|hear you|facetime|созвон|позвон)\b/i,
    flag: 'call',
    leads: ['Oh you want to hear me? 🥰 ', 'Mmm a call sounds fun 📞 ', 'I\u2019d love that 💕 '],
  },
  {
    test: /\b(yes|yeah|yep|sure|ok|okay|definitely|please|да|давай)\b/i,
    flag: 'yes',
    leads: ['Good boy 😏 ', 'Mmm I like that answer 💋 ', 'Yesss 🔥 '],
  },
  {
    test: /\b(no|nope|nah|later|busy|нет)\b/i,
    flag: 'no',
    leads: ['Aww don\u2019t be shy 🙈 ', 'Playing hard to get? 😉 ', 'Hmm we\u2019ll see 😌 '],
  },
];

/**
 * The fixed storyline (linear beats). The arc is short on purpose so the call
 * offer arrives early (~7th message): greet -> name -> what he liked -> get to
 * know each other closer -> lonely -> wish to hear voice -> OFFER CALL -> nudge
 * -> soft flirt loop. There are NO photos in the linear flow: the author only
 * sends a photo when he actually asks (see PHOTO_BEAT + keyword override).
 */
export const BEATS = [
  // 0 - greet back, acknowledge his attention
  {
    id: 'greet',
    variants: [
      "Hey you 😏 I saw all those likes... you really can't get enough of me, can you?",
      'Mmm, a fan with great taste. I noticed every single one of your likes 💋',
      "So it's you behind all those hearts 😍 you definitely know how to get my attention",
      "Hi cutie 🔥 your likes made my whole day, tell me a little about yourself?",
      "I see you've been watching me a lot 👀 don't be shy now, say hi properly 😘",
      'Finally you message me 😊 I was hoping the cutie with all the likes would say something',
    ],
  },
  // 1 - ask his name
  {
    id: 'name',
    variants: [
      "So... what should I call you, handsome? 😊",
      "Tell me your name, I want to know who's been adoring me 💕",
      "What's your name, cutie? I like to know who I'm talking to 😏",
      "Mmm before we go further, what do I call you? 😘",
      "Give me a name to put to all this charm 😍",
      "What's your name, babe? I promise I'll remember it 💋",
    ],
  },
  // 2 - ask what he liked most about her
  {
    id: 'liked-most',
    variants: [
      "So tell me honestly... what did you like most about me? 😏",
      "Out of all my videos, what caught your eye the most? 👀",
      "Be honest, what's your favorite thing about me? 💋",
      "Mmm I'm curious... what made you fall for me? 😘",
      "What was it that made you keep watching me, hmm? 🔥",
      "Tell me what you like most about me, don't hold back 😈",
    ],
  },
  // 3 - propose getting to know each other closer
  {
    id: 'closer',
    variants: [
      "Mmm I like you already... let's get to know each other a little closer? �",
      "Tell me more about you, I want us to get closer tonight �",
      "I love feeling close to someone � tell me a little secret about you",
      "Let's get closer, hmm? Tell me what you're really into �",
      "I want to actually know you, not just chat... open up to me �",
      "Come a little closer, tell me something real about yourself 😈",
    ],
  },
  // 4 - admit she's a little lonely (warms it up)
  {
    id: 'lonely',
    variants: [
      "Honestly? I've been a little bored and lonely tonight 🙈",
      "It's kinda quiet here and I could use some company 😌",
      "I get lonely after filming, it's nice to talk to someone like you 💕",
      "Between us... I don't really like being alone at night 😏",
      "I was just lying here wishing someone interesting would text me 😘",
      "You caught me in a clingy mood tonight 😈 lucky you",
    ],
  },
  // 5 - wish to hear his voice (sets up the call)
  {
    id: 'wish-voice',
    variants: [
      "Texting is fun, but I really wish I could hear your voice 🥺",
      "Mmm I bet your voice is as cute as you are 😘",
      "I'm getting tired of typing... I'd rather talk to you 😏",
      "Wouldn't it be nicer to actually hear each other? 💕",
      "I want to hear how you sound when you flirt with me 😈",
      "Words aren't enough... I want your voice 🔥",
    ],
  },
  // 6 - PROPOSE a call (~7th message)
  {
    id: 'offer-call',
    offersCall: true,
    variants: [
      "Let's get on a call right now 📞 tap the phone up top 😘",
      "Call me, cutie 💕 I just put a phone icon up there for you",
      "I want to hear you 🥰 hit the call button at the top",
      "Enough texting... call me 😏 the phone is right up top",
      "Press that little phone icon and let's talk for real 🔥",
      "I'm waiting by my phone 😈 call me now, please?",
    ],
  },
  // 7 - encourage to call (if he hesitates)
  {
    id: 'nudge-call',
    offersCall: true,
    variants: [
      "Don't be shy, just tap the phone and call me 🥺",
      "I promise I don't bite... much 😈 call me?",
      "The phone icon is right there, cutie, use it 😘",
      "I'm getting impatient to hear you 🔥 call me already",
      "Come on, one little call won't hurt 💕",
      "I'll be so happy if you call me right now 🙈",
    ],
  },
  // 8+ - soft flirt loop, keeps nudging toward the call
  {
    id: 'flirt-loop-1',
    offersCall: true,
    variants: [
      "You're really fun to talk to, you know 💕",
      "I keep smiling at my phone because of you 🙈",
      "Tell me more about you, I'm hooked 😏",
      "You're different from the others, I like it 😘",
      "I could talk to you all night 🔥",
      "You've got me curious about you now 😈",
    ],
  },
  {
    id: 'flirt-loop-2',
    offersCall: true,
    variants: [
      "So are you going to call me or keep me waiting? 😏",
      "I still want to hear that voice of yours 🥺",
      "My phone is right here, just saying 📞",
      "Don't make a girl beg... or do 😈",
      "I'd love to see your face on that call 💕",
      "Come closer to me, call me 🔥",
    ],
  },
  {
    id: 'flirt-loop-3',
    offersCall: true,
    variants: [
      "Did I tell you how much I like your attention? �",
      "You're spoiling me with all this talk, I love it 💕",
      "Tell me what you're thinking right now 😏",
      "I'm blushing again because of you �",
      "You really know how to keep a girl interested 😘",
      "Mmm keep talking to me like that 🔥",
    ],
  },
  {
    id: 'flirt-loop-4',
    offersCall: true,
    variants: [
      "I mean it, a call would make my night 🥰",
      "We've been texting so long, let's hear each other 📞",
      "I feel like we'd click even more on a call 💕",
      "Be brave and call me, cutie 😏",
      "I'll make it worth your while, promise 😈",
      "Just one call, for me? 🥺",
    ],
  },
  {
    id: 'flirt-loop-5',
    offersCall: true,
    variants: [
      "Whatever you decide, I'm happy you're here �",
      "You've made tonight so much better, truly 🥰",
      "I'll be right here whenever you want me �",
      "Don't disappear on me now, cutie 😘",
      "I'm still hoping for that call though �",
      "Talk to me more, I'm not done with you �",
    ],
  },
];

/**
 * Photo beat - NOT part of the linear storyline. The author only sends a photo
 * when the user explicitly asks for one (keyword override in advanceConversation).
 */
export const PHOTO_BEAT = Object.freeze({
  id: 'send-photo',
  sendsPhoto: true,
  variants: [
    "There you go... just for you �",
    "Here 🙈 don't share it with anyone, okay?",
    "This one's only for you, cutie �",
    "Hope you like what you see �",
    "Took this just now... be nice �",
    "Surprise 😍 this is how I look right now",
  ],
});

/* Extra intents layered on top of the base KEYWORDS (topics + moods). */
const EXTRA_KEYWORDS = [
  { flag: 'askName', test: /\b(your name|what.*call you|who are you|как.*зовут|тво[её] имя)\b/i },
  { flag: 'askAge', test: /\b(how old|your age|сколько.*лет|твой возраст)\b/i },
  { flag: 'askLocation', test: /\b(where are you|where.*from|your city|what country|откуда|где ты)\b/i },
  { flag: 'askJob', test: /\b(what.*do for|your job|do you work|profession|чем.*занима|кем.*работ)\b/i },
  { flag: 'askHobby', test: /\b(hobby|hobbies|free time|for fun|like to do|увлеч|хобби)\b/i },
  { flag: 'askRelationship', test: /\b(boyfriend|husband|single|married|relationship|taken|парень|муж|замужем|свободна)\b/i },
  { flag: 'askDoing', test: /\b(what.*you doing|what.*up to|чем.*занят|что делаешь)\b/i },
  { flag: 'sexual', test: /\b(sex|naked|nude|boobs|tits|ass|turn me on|kiss|touch|секс|голая|поцелу)\b/i },
  { flag: 'sad', test: /\b(sad|lonely|depress|bored|tired|down|грустно|скучно|устал|одинок)\b/i },
  { flag: 'bye', test: /\b(bye|goodbye|gtg|good ?night|see ya|later|пока|до встречи|спокойной)\b/i },
];

/* Answers to direct questions about her (she feels like a real person). */
const TOPICS = {
  askName: [
    "I'm Mia 😊 but you can call me whatever you like 😏",
    "Mia 💕 now I told you mine, be nice to me okay?",
    "Call me Mia 😘 I like the way you'll say it",
  ],
  askAge: [
    "I'm 23 😇 trouble, but the fun kind",
    "Old enough to know what I want 😏 23 if you must know",
    "23 🙈 why, do you like them older or younger?",
  ],
  askLocation: [
    "I'm in LA right now 🌴 wish you were closer",
    "Los Angeles 😌 but I travel a lot for shoots",
    "California 😘 it's warm here... like me",
  ],
  askJob: [
    "I make videos and... other spicy content 😈 you've seen some 🙈",
    "Model and content creator 💕 keeps me busy",
    "Let's just say I'm very good at getting attention 😏",
  ],
  askHobby: [
    "Dancing, photoshoots, and teasing cute guys like you 🙈",
    "Yoga, late nights, and a little mischief 😈",
    "Making content, gym, and flirting apparently 😘",
  ],
  askRelationship: [
    "Single and a little bored 😏 that's why I'm talking to you",
    "No boyfriend 🙈 I get lonely though... keep me company?",
    "Very single 💕 are you offering to fix that?",
  ],
  askDoing: [
    "Just lying in bed, kinda lonely 🙈 keep me company?",
    "Relaxing after a shoot 😌 thinking who to text... you won",
    "Nothing exciting 😏 you're the most fun thing right now",
  ],
};

/* Flirty acknowledgements for general / off-script messages. */
const SMALLTALK = [
  "Mmm I like talking to you 💕",
  "You're cute, you know that? 🙈",
  "Hehe you keep making me smile 😊",
  "Ooh I like where your head's at 😏",
  "You're trouble, aren't you 😈",
  "Keep talking, I'm enjoying this 🔥",
  "You're different, I like it 😘",
  "Mmm go on 💋",
];

/* Questions she asks back to keep the user typing. */
const FOLLOWUPS = [
  "So tell me, what are you into? 😏",
  "What would you do if I was there with you right now? 😈",
  "Are you always this charming, or am I special? 🙈",
  "What are you wearing right now? 👀",
  "Do you think about me when we're not talking? 💕",
  "What's something naughty you've never told anyone? 😏",
  "If you could do anything with me tonight, what would it be? 🔥",
  "Tell me a secret, I'll keep it 😘",
];

/* Photo request - teasing deferrals (the "earn it" loop). */
const PHOTO_DEFLECT = [
  "Mmm let me think about it 😏 be patient",
  "Not right now, I need to get ready first 🙈",
  "Ooh someone's eager 😘 give me time to make it perfect for you",
  "I'm not really in the right place for that yet 😅 soon though",
  "Earn it a little more first, cutie 😈",
  "I need to freshen up before I send anything 💋 keep me company meanwhile?",
  "Patience 😏 the wait makes it so much better",
  "Hmm... convince me a little more first 🙈",
];

/* Photo request - granted lines (sent with a real photo). */
const PHOTO_GRANT = [
  "Okay okay... here, just for you 😘",
  "Fine, you earned it 🙈 don't share it!",
  "This one's only for you, cutie 💋",
  "Mmm hope you like what you see 🔥",
  "Took this just for you... be nice 😏",
  "Surprise 😍 this is me right now",
];

/* Call request - coy deferral / acceptance. */
const CALL_DEFLECT = [
  "I'd love to but let me get comfy first 😏",
  "Soon 🙈 I need a minute to look cute for you",
  "Mmm not yet... build it up a little more for me 😘",
  "Patience cutie, I want our first call to be perfect 💕",
];
const CALL_GRANT = [
  "Okay let's do it 📞 tap the phone up top 😘",
  "Yesss call me 🥰 the phone icon is right there",
  "I want to hear you too 🔥 hit the call button up top",
  "Mmm fine, you win 😈 call me now",
];

/* Reactions when the user sends his own photo/video. */
const MEDIA_REACTIONS = [
  "Mmm is that for me? 😍",
  "Ooh I like what I'm seeing 😏",
  "Someone's confident 🙈 I love it",
  "You're making me blush 💗 send more?",
  "Mmm cutie 🔥 my turn soon maybe",
];

/* Soft replies when the user seems sad / bored. */
const SAD_REPLIES = [
  "Aww don't be down 🥺 I'm here for you",
  "Hey, talk to me 💕 I'll cheer you up",
  "Let me make your night better 😘 tell me what's wrong",
  "Come here, I don't like seeing you like that 🙈",
];

/* Goodbye replies. */
const BYE_REPLIES = [
  "leaving me already? 🥺 text me soon okay",
  "okay cutie 😘 don't make me wait too long",
  "fine but you owe me a call next time 😏",
  "miss me while you're gone 💕",
];

/* Affection thresholds that gate the rewards. */
const PHOTO_AFFECTION_MIN = 4;
const CALL_AFFECTION_MIN = 3;

const TOPIC_ORDER = [
  'askName', 'askAge', 'askLocation', 'askJob', 'askHobby', 'askRelationship', 'askDoing',
];
const firstTopic = (flags) => TOPIC_ORDER.find((t) => flags[t]) || null;

/** Detect intent flags + a reactive lead from the user's text. */
function detect(text) {
  const t = String(text || '');
  const flags = {};
  let lead = '';
  for (const k of [...KEYWORDS, ...EXTRA_KEYWORDS]) {
    if (k.test.test(t)) {
      flags[k.flag] = true;
      if (!lead && k.leads) lead = pick(k.leads);
    }
  }
  return { flags, lead };
}

/**
 * Produce the author's next action + updated state. Priority:
 *   user media -> photo request (gated) -> call request (gated) ->
 *   direct question (topic) -> mood -> flirty banter / storyline question.
 *
 * @param {Object} chat   - { step, flags, affection, photoRequests, photosSent, lastLine, callOffered }
 * @param {{ text?: string, media?: Object }} userMsg
 * @returns {AuthorAction}
 */
export function advanceConversation(chat, userMsg) {
  const text = userMsg?.text || '';
  const media = userMsg?.media || null;
  const last = chat.lastLine || '';

  const det = detect(text);
  const flags = { ...(chat.flags || {}), ...det.flags };
  const lead = det.lead;

  let step = Math.min(chat.step ?? 0, BEATS.length - 1);
  let photoRequests = chat.photoRequests ?? 0;
  let photosSent = chat.photosSent ?? 0;

  // Affection (engagement) bookkeeping - drives whether rewards are unlocked.
  let affection = chat.affection ?? 0;
  affection += 0.5;
  if (det.flags.complimented) affection += 2;
  if (det.flags.yes) affection += 1;
  if (det.flags.sexual) affection += 1;
  if (det.flags.no) affection -= 0.5;
  affection = clamp(affection, 0, 100);

  const out = (raw, opts = {}) => {
    const t = raw.trim();
    return {
      text: t,
      media: opts.media || null,
      offersCall: !!opts.offersCall,
      flags,
      affection,
      photoRequests,
      photosSent,
      step,
      lastLine: t,
    };
  };

  // 1) user sent his own media -> warm reaction
  if (media) return out(pick(MEDIA_REACTIONS, last));

  // 2) photo request -> earned reward (sometimes deferred to keep him hooked)
  if (det.flags.photo) {
    photoRequests += 1;
    const ready =
      affection >= PHOTO_AFFECTION_MIN && (photoRequests >= 2 || Math.random() < 0.45);
    if (ready) {
      photosSent += 1;
      photoRequests = 0;
      affection = clamp(affection - 2, 0, 100); // next one must be earned again
      return out(pick(PHOTO_GRANT, last), { media: randomAuthorPhoto() });
    }
    return out(pick(PHOTO_DEFLECT, last));
  }

  // 3) call request -> offer once warmed up, otherwise coyly defer
  if (det.flags.call) {
    if (affection >= CALL_AFFECTION_MIN || chat.callOffered) {
      return out(pick(CALL_GRANT, last), { offersCall: true });
    }
    return out(pick(CALL_DEFLECT, last));
  }

  // 4) direct question about her -> answer + maybe a follow-up
  const topic = firstTopic(det.flags);
  if (topic) {
    const follow = Math.random() < 0.5 ? ` ${pick(FOLLOWUPS, last)}` : '';
    return out(`${lead}${pick(TOPICS[topic], last)}${follow}`);
  }

  // 5) moods
  if (det.flags.bye) return out(`${lead}${pick(BYE_REPLIES, last)}`);
  if (det.flags.sad) return out(pick(SAD_REPLIES, last));

  // 6) default: flirty banter; ~55% of the time advance the storyline question
  if (step < BEATS.length && Math.random() < 0.55) {
    const beat = BEATS[step];
    step = Math.min(step + 1, BEATS.length - 1);
    const ack = lead || (Math.random() < 0.6 ? `${pick(SMALLTALK, last)} ` : '');
    return out(`${ack}${pick(beat.variants, last)}`, { offersCall: !!beat.offersCall });
  }
  const ack = lead || `${pick(SMALLTALK, last)} `;
  return out(`${ack}${pick(FOLLOWUPS, last)}`);
}

/** Total number of scripted beats (for callers/tests). */
export const BEAT_COUNT = BEATS.length;
